import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
    Card,
    CardBody,
    Button,
    Input,
    Textarea,
    Tabs,
    Tab,
} from "@heroui/react";
import { LuSave } from "react-icons/lu";
import { useFetchData, usePostData } from "@/hooks";
import { PageHeader, ThemedText } from "@/components/ui";
import { LOGO_SQUARE_URL } from "@/constants";
import { showToast } from "@/utils";

const Settings = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("company");

    const { data: companyData, isLoading: loadingCompany } =
        useFetchData("app-settings/company");
    const { data: receiptData, isLoading: loadingReceipt } =
        useFetchData("app-settings/receipt");

    const { mutate: updateSetting, isLoading: isSaving } =
        usePostData("app-settings/update");

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { isDirty },
    } = useForm({
        defaultValues: {
            company_name: "",
            company_address: "",
            company_phone: "",
            company_email: "",
            receipt_title: "",
            receipt_store_name: "",
            receipt_store_location: "",
            receipt_footer: "",
            receipt_disclaimer: "",
        },
    });

    useEffect(() => {
        if (companyData || receiptData) {
            reset({
                company_name: companyData?.name || "",
                company_address: companyData?.address || "",
                company_phone: companyData?.phone || "",
                company_email: companyData?.email || "",
                receipt_title: receiptData?.title || "",
                receipt_store_name: receiptData?.store_name || "",
                receipt_store_location: receiptData?.store_location || "",
                receipt_footer: receiptData?.footer || "",
                receipt_disclaimer: receiptData?.disclaimer || "",
            });
        }
    }, [companyData, receiptData, reset]);

    const watched = watch();

    const onSubmit = async (data) => {
        const saveCompany = () =>
            new Promise((resolve, reject) => {
                updateSetting(
                    {
                        key: "company",
                        value: {
                            name: data.company_name,
                            address: data.company_address,
                            phone: data.company_phone,
                            email: data.company_email,
                        },
                    },
                    { method: "put", onSuccess: resolve, onError: reject },
                );
            });

        const saveReceipt = () =>
            new Promise((resolve, reject) => {
                updateSetting(
                    {
                        key: "receipt",
                        value: {
                            title: data.receipt_title,
                            store_name: data.receipt_store_name,
                            store_location: data.receipt_store_location,
                            footer: data.receipt_footer,
                            disclaimer: data.receipt_disclaimer,
                        },
                    },
                    { method: "put", onSuccess: resolve, onError: reject },
                );
            });

        try {
            await saveCompany();
            await saveReceipt();
            showToast("success", "Berhasil", "Pengaturan berhasil disimpan.");
            reset(data);
            navigate("/");
        } catch {
            showToast("error", "Gagal", "Gagal menyimpan pengaturan.");
        }
    };

    const isLoading = loadingCompany || loadingReceipt;

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Kelola Aplikasi"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Kelola Aplikasi" },
                    ]}
                />
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuSave size={16} />}
                    isDisabled={!isDirty || isSaving}
                    isLoading={isSaving}
                    onPress={handleSubmit(onSubmit)}
                >
                    Simpan Pengaturan
                </Button>
            </div>

            {isLoading ? (
                <div className="flex h-48 items-center justify-center">
                    <ThemedText variant="secondary">
                        Memuat pengaturan...
                    </ThemedText>
                </div>
            ) : (
                <>
                    <Tabs
                        selectedKey={activeTab}
                        onSelectionChange={setActiveTab}
                        variant="solid"
                        size="sm"
                    >
                        <Tab key="company" title="Info Perusahaan" />
                        <Tab key="receipt" title="Struk" />
                    </Tabs>

                    {activeTab === "company" && (
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            {/* Form */}
                            <Card
                                className="border-default-200 border"
                                shadow="none"
                            >
                                <CardBody className="flex flex-col gap-3 p-4">
                                    <Input
                                        label="Nama Perusahaan"
                                        variant="bordered"
                                        {...register("company_name")}
                                    />
                                    <Input
                                        label="Alamat"
                                        variant="bordered"
                                        {...register("company_address")}
                                    />
                                    <div className="grid grid-cols-2 gap-3">
                                        <Input
                                            label="Telepon"
                                            variant="bordered"
                                            {...register("company_phone")}
                                        />
                                        <Input
                                            label="Email"
                                            variant="bordered"
                                            {...register("company_email")}
                                        />
                                    </div>
                                </CardBody>
                            </Card>

                            {/* Preview */}
                            <Card
                                className="border-default-200 border"
                                shadow="none"
                            >
                                <CardBody className="p-4">
                                    <ThemedText
                                        size="xs"
                                        variant="secondary"
                                        className="mb-3 font-medium uppercase tracking-wide"
                                    >
                                        Preview Kop Surat
                                    </ThemedText>
                                    <div className="border-b-2 border-black pb-3">
                                        <div className="flex items-end justify-between">
                                            <div>
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={LOGO_SQUARE_URL}
                                                        alt="Logo"
                                                        className="h-10 w-10 object-contain"
                                                    />
                                                    <h1 className="text-xl font-bold">
                                                        {watched.company_name ||
                                                            "Nama Perusahaan"}
                                                    </h1>
                                                </div>
                                                <p className="text-default-600 text-xs">
                                                    {watched.company_address ||
                                                        "Alamat"}
                                                </p>
                                                <p className="text-default-600 text-xs">
                                                    Telp:{" "}
                                                    {watched.company_phone ||
                                                        "-"}{" "}
                                                    | Email:{" "}
                                                    {watched.company_email ||
                                                        "-"}
                                                </p>
                                            </div>
                                            <div className="text-right text-lg font-bold">
                                                LAPORAN
                                            </div>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    )}

                    {activeTab === "receipt" && (
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            {/* Form */}
                            <Card
                                className="border-default-200 border"
                                shadow="none"
                            >
                                <CardBody className="flex flex-col gap-3 p-4">
                                    <Input
                                        label="Judul Struk"
                                        placeholder="RECEIPT"
                                        variant="bordered"
                                        {...register("receipt_title")}
                                    />
                                    <Input
                                        label="Nama Toko"
                                        placeholder="Telor Telur"
                                        variant="bordered"
                                        {...register("receipt_store_name")}
                                    />
                                    <Input
                                        label="Lokasi Toko"
                                        placeholder="Medan, Indonesia"
                                        variant="bordered"
                                        {...register("receipt_store_location")}
                                    />
                                    <Input
                                        label="Footer"
                                        variant="bordered"
                                        {...register("receipt_footer")}
                                    />
                                    <Textarea
                                        label="Disclaimer"
                                        variant="bordered"
                                        minRows={2}
                                        {...register("receipt_disclaimer")}
                                    />
                                </CardBody>
                            </Card>

                            {/* Preview */}
                            <Card
                                className="border-default-200 border"
                                shadow="none"
                            >
                                <CardBody className="p-4">
                                    <ThemedText
                                        size="xs"
                                        variant="secondary"
                                        className="mb-3 font-medium uppercase tracking-wide"
                                    >
                                        Preview Struk
                                    </ThemedText>
                                    <div className="mx-auto max-w-[280px] font-mono text-[12px] leading-normal">
                                        <div className="mb-2 text-center">
                                            <p className="text-[13px] font-bold">
                                                {watched.receipt_title || "RECEIPT"}
                                            </p>
                                            <p>
                                                {watched.receipt_store_name ||
                                                    "Nama Toko"}
                                            </p>
                                            <p>
                                                {watched.receipt_store_location ||
                                                    "Lokasi"}
                                            </p>
                                        </div>
                                        <div className="my-2 border-t border-dashed border-black" />
                                        <div className="space-y-0.5">
                                            <div className="flex justify-between">
                                                <span>No.</span>
                                                <span>SI/0001/0001</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Customer</span>
                                                <span>CONTOH</span>
                                            </div>
                                        </div>
                                        <div className="my-2 border-t border-dashed border-black" />
                                        <div>
                                            <p>1. Telur Arab</p>
                                            <div className="flex justify-between pl-3">
                                                <span>
                                                    300 butir x Rp 2.100
                                                </span>
                                                <span>Rp 630.000</span>
                                            </div>
                                        </div>
                                        <div className="my-2 border-t border-dashed border-black" />
                                        <div className="flex justify-between text-[13px] font-bold">
                                            <span>TOTAL</span>
                                            <span>Rp 630.000</span>
                                        </div>
                                        <div className="my-2 text-center text-[13px] font-bold">
                                            PAID
                                        </div>
                                        <div className="my-2 border-t border-dashed border-black" />
                                        <div className="text-center">
                                            <p>
                                                {watched.receipt_footer ||
                                                    "Footer text"}
                                            </p>
                                        </div>
                                        <div className="my-2 border-t border-dashed border-black" />
                                        <div className="text-center text-[11px]">
                                            <p>
                                                {watched.receipt_disclaimer ||
                                                    "Disclaimer text"}
                                            </p>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Settings;
